/**
 * Intercom identity helpers.
 *
 * The Messenger is installed through GTM (container GTM-KCWRJ43): the install
 * tag boots it anonymously on every app pageview, and a second tag attaches the
 * user attributes only when the `login` dataLayer event fires. Help center
 * content whose audience is restricted to logged-in users is served based on
 * that identity, so a missed `login` event means the reader silently loses
 * access to most of help.trajaan.io.
 *
 * These helpers drive the same `update` from the app itself, so identification
 * no longer depends on a single event firing, and clear the identity on sign
 * out so it is not inherited by the next person using the browser.
 */

type IntercomFn = (method: string, payload?: Record<string, unknown>) => void;

type IntercomUser = {
  id: string;
  email?: string;
  first_name?: string;
  last_name?: string;
};

type IntercomCompany = {
  id: string;
  company_name?: string;
  industry?: string;
};

function getIntercom(): IntercomFn | undefined {
  const intercom = (window as {Intercom?: IntercomFn}).Intercom;
  return typeof intercom === 'function' ? intercom : undefined;
}

/**
 * The app id lives in the GTM tag, not in the frontend bundle. Reading it back
 * off the settings GTM writes keeps the two from drifting apart.
 */
function getAppId(): string | undefined {
  return (window as {intercomSettings?: {app_id?: string}}).intercomSettings
    ?.app_id;
}

function buildName(user: IntercomUser): string | undefined {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return name || undefined;
}

export function identifyIntercomUser(
  user: IntercomUser | null | undefined,
  company?: IntercomCompany | null,
): void {
  const intercom = getIntercom();
  if (!intercom || !user?.id) {
    return;
  }

  intercom('update', {
    user_id: user.id,
    email: user.email,
    name: buildName(user),
    ...(company?.id
      ? {
          company: {
            company_id: company.id,
            name: company.company_name,
            industry: company.industry,
          },
        }
      : {}),
  });
}

/**
 * Ends the Messenger session and starts a fresh anonymous one. Without this the
 * `intercom-session-*` cookie — which is scoped to `.trajaan.io` and therefore
 * shared with help.trajaan.io — keeps the previous user's identity after they
 * sign out.
 */
export function resetIntercomIdentity(): void {
  const intercom = getIntercom();
  if (!intercom) {
    return;
  }

  const appId = getAppId();
  intercom('shutdown');

  if (appId) {
    intercom('boot', {app_id: appId});
  }
}
